/**
 * Execution bulk-search descriptor — adapts SearchExecutions to
 * BulkSearchResults. The queried ids go in as executionIds; status/state/type
 * are server-side PropertyFilter refinements (type via
 * additionalFilters.executionType).
 */
import Badge from '@cloudscape-design/components/badge';
import { PropertyFilterProps } from '@cloudscape-design/components/property-filter';
import { titleCase, fullTimestamp } from 'src/store/slices/paymentDetail/paymentDetailFormat';
import { formatLatency } from 'src/utils/formatters';
import { DetailLink } from 'src/components/common/DetailLink';
import {
  ApiExecutionSummary,
  ExecutionFilters,
  SearchExecutionsInput,
} from 'src/store/slices/searchExecutions/searchExecutionsApi';
import {
  searchExecutions, searchExecutionsRegion, clearSearchExecutions,
} from 'src/store/slices/searchExecutions/searchExecutionsSlice';
import {
  BulkSearchArgs,
  BulkSearchDescriptor,
  StatusCell,
  dash,
  firstOf,
  joinList,
  refTypeColumns,
} from './bulkSearchShared';

const FILTERING_PROPERTIES: PropertyFilterProps.FilteringProperty[] = [
  { key: 'status', propertyLabel: 'Status', groupValuesLabel: 'Status values', operators: ['='] },
  { key: 'state', propertyLabel: 'State', groupValuesLabel: 'State values', operators: ['='] },
  { key: 'type', propertyLabel: 'Type', groupValuesLabel: 'Type values', operators: ['='] },
];

/** Columns shown by default; every other column stays toggleable in Preferences. */
const DEFAULT_VISIBLE = ['id', 'executionType', 'beaconStatus', 'system', 'direction', 'artifactId', 'payments', 'totalLatency', 'updatedAt'];

const buildInput = ({ ids, pageNumber, pageSize, filters }: BulkSearchArgs): SearchExecutionsInput => {
  const f: ExecutionFilters = { executionIds: ids };
  const status = firstOf(filters, 'status');
  const state = firstOf(filters, 'state');
  if (status) f.beaconExecutionStatus = status;
  if (state) f.beaconExecutionState = state;
  if (filters.type?.length) f.additionalFilters = { executionType: filters.type };
  return { filters: f, pagination: { pageNumber, pageSize } };
};

export const executionSearchDescriptor: BulkSearchDescriptor<ApiExecutionSummary> = {
  entity: 'execution',
  title: 'Bulk execution search',
  path: '/executions',
  bulkPlaceholder: 'Enter multiple execution IDs, one per line',
  filteringProperties: FILTERING_PROPERTIES,
  columns: [
    {
      id: 'id',
      header: 'Execution id',
      cell: (e) => <DetailLink to={`/execution?id=${encodeURIComponent(e.id)}`} newTab>{e.id}</DetailLink>,
    },
    { id: 'executionType', header: 'Type', cell: (e) => (e.executionType ? <Badge>{e.executionType}</Badge> : '-') },
    { id: 'beaconStatus', header: 'Beacon status', cell: (e) => <StatusCell state={e.beaconExecutionState} label={e.beaconExecutionStatus} /> },
    { id: 'beaconState', header: 'Beacon state', cell: (e) => dash(e.beaconExecutionState) },
    { id: 'system', header: 'System', cell: (e) => dash(e.currentSystem) },
    { id: 'direction', header: 'Direction', cell: (e) => (e.currentDirection ? titleCase(e.currentDirection) : '-') },
    { id: 'artifactId', header: 'Artifact', cell: (e) => (e.artifactId ? <DetailLink to={`/artifact?id=${encodeURIComponent(e.artifactId)}`} newTab>{e.artifactId}</DetailLink> : '-') },
    { id: 'payments', header: 'Payments', cell: (e) => e.paymentIds?.length ?? 0 },
    { id: 'workflowArn', header: 'Workflow ARN', cell: (e) => dash(e.workflowArn) },
    { id: 'parentExecutionId', header: 'Parent execution', cell: (e) => dash(e.parentExecutionId) },
    { id: 'currentStatusAge', header: 'Status age', cell: (e) => formatLatency(e.currentStatusAgeMs) },
    { id: 'totalLatency', header: 'Total latency', cell: (e) => formatLatency(e.totalLatencyMs) },
    { id: 'correlationTags', header: 'Correlation tags', cell: (e) => joinList(e.correlationTags) },
    { id: 'createdAt', header: 'Created', cell: (e) => (e.createdAt ? fullTimestamp(e.createdAt) : '-') },
    { id: 'updatedAt', header: 'Updated', cell: (e) => (e.updatedAt ? fullTimestamp(e.updatedAt) : '-') },
  ],
  defaultVisibleColumns: DEFAULT_VISIBLE,
  // One column per execution type — header = type, cell = this execution's id.
  dynamicColumns: (items) => refTypeColumns(items, (e) => [{ type: e.executionType, id: e.id }]),
  run: (dispatch, args) => { dispatch(searchExecutions(buildInput(args))); },
  runRegion: (dispatch, { region, ...args }) => {
    dispatch(searchExecutionsRegion({ input: buildInput(args), region }));
  },
  clear: (dispatch) => { dispatch(clearSearchExecutions()); },
  select: (s) => ({
    items: s.searchExecutions.executions,
    total: s.searchExecutions.total,
    loading: s.searchExecutions.loading,
    error: s.searchExecutions.error,
    regionResults: s.searchExecutions.regionResults,
    searchedRegions: s.searchExecutions.searchedRegions,
    regionErrors: s.searchExecutions.regionErrors,
    loadingRegions: s.searchExecutions.loadingRegions,
  }),
};
